import React, { useState } from "react";
import { FiDollarSign, FiCalendar, FiFileText, FiCreditCard, FiTag, FiHash } from "react-icons/fi";
import {
  useCreateCashflowMutation,
  useDeleteCashflowMutation,
  useGetCashflowsQuery,
  useUpdateCashflowMutation,
} from "../services/api";
import SectionHeader from "../components/ui/SectionHeader";
import { getApiErrorMessage } from "../utils/apiError";

const CATEGORIES = [
  { value: "salary", label: "Salary / Payroll" },
  { value: "rent", label: "Rent" },
  { value: "utilities", label: "Utilities (WiFi, Electric, Water)" },
  { value: "supplies", label: "Office Supplies" },
  { value: "maintenance", label: "Maintenance & Repairs" },
  { value: "marketing", label: "Marketing" },
  { value: "transport", label: "Transport" },
  { value: "other", label: "Other" },
];

const PAYMENT_METHODS = ["cash", "bank_transfer", "card", "mobile_money"];

const CATEGORY_COLORS = {
  salary: "#6366f1",
  rent: "#f59e0b",
  utilities: "#3b82f6",
  supplies: "#10b981",
  maintenance: "#ef4444",
  marketing: "#8b5cf6",
  transport: "#14b8a6",
  other: "#6b7280",
};

const EMPTY_FORM = {
  category: "salary",
  description: "",
  amount: "",
  payment_method: "cash",
  reference_number: "",
  flow_date: new Date().toISOString().slice(0, 10),
  notes: "",
};

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("en-ET", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const CategoryDot = ({ category }) => (
  <span
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: CATEGORY_COLORS[category] || "#6b7280",
      marginRight: 6,
      flexShrink: 0,
    }}
  />
);

const ExpensesPage = () => {
  const { data, isLoading } = useGetCashflowsQuery();
  const [createCashflow] = useCreateCashflowMutation();
  const [updateCashflow] = useUpdateCashflowMutation();
  const [deleteCashflow] = useDeleteCashflowMutation();

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cashflows = data?.data || [];

  const filtered =
    filterCategory === "all"
      ? cashflows
      : cashflows.filter((cf) => cf.category === filterCategory);

  const totalAll = cashflows.reduce((sum, cf) => sum + Number(cf.amount), 0);
  const totalFiltered = filtered.reduce((sum, cf) => sum + Number(cf.amount), 0);

  // Breakdown by category
  const breakdown = CATEGORIES.map((cat) => ({
    ...cat,
    total: cashflows
      .filter((cf) => cf.category === cat.value)
      .reduce((sum, cf) => sum + Number(cf.amount), 0),
  })).filter((cat) => cat.total > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setIsSubmitting(true);
    try {
      if (editingId) {
        await updateCashflow({ id: editingId, ...form }).unwrap();
      } else {
        await createCashflow(form).unwrap();
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, "Unable to save cash flow entry."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (cf) => {
    setEditingId(cf.id);
    setForm({
      category: cf.category,
      description: cf.description,
      amount: cf.amount,
      payment_method: cf.payment_method,
      reference_number: cf.reference_number || "",
      flow_date: cf.flow_date || cf.created_at?.slice(0, 10) || "",
      notes: cf.notes || "",
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this expense entry?")) {
      await deleteCashflow(id);
    }
  };

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Cash Flow & Expenses"
        title="Track every outgoing payment — salaries, rent, utilities, and more."
      />

      {/* ── Summary cards ── */}
      <div className="stats-row" style={{ marginBottom: "1.5rem" }}>
        <div className="stat-card">
          <p className="eyebrow">Total Expenses</p>
          <h2>{formatCurrency(totalAll)}</h2>
          <small>{cashflows.length} entries</small>
        </div>
        {breakdown.map((cat) => (
          <div className="stat-card" key={cat.value}>
            <p className="eyebrow" style={{ display: "flex", alignItems: "center" }}>
              <CategoryDot category={cat.value} />
              {cat.label}
            </p>
            <h2>{formatCurrency(cat.total)}</h2>
          </div>
        ))}
      </div>

      <div className="dashboard-grid dashboard-grid--wide">
        {/* ── Expense list ── */}
        <section className="panel" style={{ gridColumn: "span 2" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3>Expense entries</h3>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <label style={{ fontSize: "0.8rem", color: "var(--text-muted, #888)" }}>Filter:</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                style={{ fontSize: "0.85rem" }}
              >
                <option value="all">All categories</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isLoading && <p>Loading…</p>}

          {filtered.length === 0 && !isLoading && (
            <p style={{ color: "var(--text-muted, #888)", fontSize: "0.9rem" }}>
              No expense entries yet. Add one using the form.
            </p>
          )}

          {filtered.map((cf) => (
            <div key={cf.id} className="list-row">
              <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                <CategoryDot category={cf.category} />
                <div>
                  <strong>{cf.description}</strong>
                  <p style={{ margin: "2px 0", fontSize: "0.82rem", color: "var(--text-muted, #888)" }}>
                    {CATEGORIES.find((c) => c.value === cf.category)?.label} &bull;{" "}
                    {cf.payment_method?.replace("_", " ")} &bull; {cf.flow_date}
                  </p>
                  {cf.reference_number && (
                    <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-muted, #888)" }}>
                      Ref: {cf.reference_number}
                    </p>
                  )}
                  {cf.notes && (
                    <p style={{ margin: 0, fontSize: "0.78rem", fontStyle: "italic" }}>{cf.notes}</p>
                  )}
                </div>
              </div>
              <div className="mini-metrics">
                <strong style={{ fontSize: "1rem" }}>{formatCurrency(cf.amount)}</strong>
                <button className="table-action" type="button" onClick={() => handleEdit(cf)}>
                  Edit
                </button>
                <button
                  className="table-action table-action--danger"
                  type="button"
                  onClick={() => handleDelete(cf.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}

          {filterCategory !== "all" && filtered.length > 0 && (
            <div
              style={{
                marginTop: "1rem",
                paddingTop: "0.75rem",
                borderTop: "1px solid var(--border, #e5e7eb)",
                display: "flex",
                justifyContent: "flex-end",
                fontWeight: 600,
              }}
            >
              Subtotal: {formatCurrency(totalFiltered)}
            </div>
          )}
        </section>

        {/* ── Add / Edit form ── */}
        <form className="panel form-panel form-modern" onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-section__header">
              <h3 className="form-section__title">{editingId ? "Edit Expense" : "Add New Expense"}</h3>
              <p className="form-section__subtitle">{editingId ? "Update expense entry details" : "Record a new business expense"}</p>
            </div>

            {submitError && (
              <div className="form-alert form-alert--danger">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{submitError}</span>
              </div>
            )}

            <div className="form-grid form-grid--2">
              <div className="form-group">
                <label className="form-label form-label--required">
                  <FiTag /> Category
                </label>
                <select
                  className="form-select"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  <FiCalendar /> Date
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiCalendar /></span>
                  <input
                    className="form-input"
                    type="date"
                    value={form.flow_date}
                    onChange={(e) => setForm({ ...form, flow_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group form-group--full">
                <label className="form-label form-label--required">
                  <FiFileText /> Description
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiFileText /></span>
                  <input
                    className="form-input"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="e.g. Monthly salary — Hana, Office rent for June"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  <FiDollarSign /> Amount (ETB)
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiDollarSign /></span>
                  <input
                    className="form-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="Enter amount"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  <FiCreditCard /> Payment Method
                </label>
                <select
                  className="form-select"
                  value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m.replace("_", " ").toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group form-group--full">
                <label className="form-label">
                  <FiHash /> Reference Number
                  <span className="form-label__hint">(Optional - transaction or invoice #)</span>
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiHash /></span>
                  <input
                    className="form-input"
                    value={form.reference_number}
                    onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                    placeholder="e.g. INV-2024-001, TXN-12345"
                  />
                </div>
              </div>

              <div className="form-group form-group--full">
                <label className="form-label">
                  <FiFileText /> Additional Notes
                  <span className="form-label__hint">(Optional)</span>
                </label>
                <textarea
                  className="form-textarea"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Add any additional details or notes about this expense"
                  rows="3"
                />
              </div>
            </div>

            <div className="form-actions">
              {editingId && (
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setForm(EMPTY_FORM);
                    setSubmitError("");
                  }}
                >
                  Cancel
                </button>
              )}
              <button 
                className={`btn-primary ${isSubmitting ? 'btn-primary--loading' : ''}`}
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : editingId ? "Update Expense" : "Add Expense"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpensesPage;
